import type { DiagnosticCode, RepairRuleId } from '@jsonfmt/core';

import common from './en/common.json';

/**
 * The locale registry (docs/ARCHITECTURE.md §3, ADR-0018).
 *
 * v1 ships exactly one locale, and that is the point of the file rather than an excuse for
 * not having it: adding a second language is ONE entry here plus one JSON file, and no
 * component changes. `LanguageSelector` renders `null` while `locales.length === 1`, so the
 * infrastructure exists from day one and the widget appears with the second locale — the
 * legacy build drew a full dropdown for a single language, which was dead UI.
 *
 * `en` is bundled STATICALLY (it is the fallback; a fallback that arrives over a second
 * network round trip is not a fallback). Every other locale arrives through the lazy
 * `load()` below. The `en` descriptor still carries a `load` so the registry has one shape,
 * and because the module is already in the graph that import resolves without a new chunk.
 *
 * The `label` is an ENDONYM and deliberately lives here rather than in `common.json`:
 * "Deutsch" is spelled the same in every language, so it is data about the locale, not a
 * string to be translated into it.
 */

/** Every tag the application knows. Widening it is a deliberate, typed change. */
export type LocaleTag = 'en';

/** The chrome half of the resources: exactly the shape of `en/common.json`. */
export type ChromeResource = typeof common;

/**
 * The core half, as it is SHAPED inside i18next — the return type of
 * `ui/i18n/messageCatalog.ts`'s `toMessageResource`.
 *
 * A `MessageCode` is either a `DiagnosticCode` (`syntax/unexpected-token`: contains `/`,
 * never `.`, so it survives as a flat leaf) or a `fix.${RepairRuleId}` label. i18next splits
 * keys on `.`, so the `fix.` family MUST be nested under a real `fix` object or it is
 * unreachable. This type says exactly that, and it lives here rather than in the UI because
 * `locales/types.d.ts` needs it to type `t()` and must not depend on a React module.
 */
export type MessageResource = { readonly [K in DiagnosticCode]: string } & {
  readonly fix: { readonly [K in RepairRuleId]: string };
};

/** What one locale contributes to i18next's single `common` namespace. */
export type LocaleResource = ChromeResource & { readonly message: MessageResource };

export interface LocaleDescriptor {
  readonly tag: LocaleTag;
  /** BCP-47 `<html lang>` value. Equal to `tag` today; kept separate for `pt-BR`-style tags. */
  readonly htmlLang: string;
  /** The language's name in itself. Never translated. */
  readonly label: string;
  /** Two or three letters for the collapsed selector button. */
  readonly short: string;
  /** Right-to-left scripts flip `<html dir>`. */
  readonly dir: 'ltr' | 'rtl';
  readonly load: () => Promise<ChromeResource>;
}

export const DEFAULT_LOCALE: LocaleTag = 'en';

/** The statically bundled fallback catalog. */
export const EN_COMMON: ChromeResource = common;

export const locales: readonly LocaleDescriptor[] = [
  {
    tag: 'en',
    htmlLang: 'en',
    label: 'English',
    short: 'EN',
    dir: 'ltr',
    load: async () => (await import('./en/common.json')).default,
  },
];

/** `undefined` for anything not in the registry — never a guess and never a throw. */
export function findLocale(tag: string | undefined | null): LocaleDescriptor | undefined {
  if (tag == null) return undefined;
  const lower = tag.toLowerCase();
  return (
    locales.find((l) => l.tag.toLowerCase() === lower) ??
    // `load: 'languageOnly'`: `en-GB` resolves to `en` (ADR-0018).
    locales.find((l) => l.tag.toLowerCase() === (lower.split('-')[0] ?? ''))
  );
}

/** The registry's opinion of a stored or negotiated tag, with the default as the floor. */
export function resolveLocale(tag: string | undefined | null): LocaleDescriptor {
  const found = findLocale(tag);
  if (found !== undefined) return found;
  const fallback = locales.find((l) => l.tag === DEFAULT_LOCALE);
  if (fallback === undefined) throw new Error(`locale registry has no "${DEFAULT_LOCALE}" entry`);
  return fallback;
}
